import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'

import {
  MEETING_RECAP_PAGE_TAG_TYPE,
  MEETINGS_TAG_TYPES,
  buildMeetingListTag,
  buildMeetingTag,
} from './constants'
import {
  buildListMeetingsScope,
  type ListMeetingsScopeUiFilters,
} from './list_meetings_scope'
import { transformGetMeetingSupergraphResponse } from './transformers/get_meeting_supergraph_response'
import { transformGetScheduledEventSupergraphResponse } from './transformers/get_scheduled_event_supergraph_response'
import { mapMeetingRecapPageResponse } from './transformers/meeting_recap_page_response'
import { transformMeetingsSupergraphConnectionResponse } from './transformers/meetings_supergraph_connection_response'
import { normalizeMeetingsGraphqlInputVariables } from './transformers/normalize_meetings_graphql_input_variables'
import type {
  MeetingsGraphQLResult,
  GraphQLMeetingResult,
} from './types'

/** Variables for cmd-api `listMeetings` (`Meetings_ListMeetingsInput`, camelCase filter). */
export type SupergraphListMeetingsInput = any
/**
 * Variables for the search-then-filter `meetingsPageList` (`MeetingsSearch_MeetingsInput`).
 * App builders may set `search_keyword` as a **sibling** of `filter`; it is merged into
 * `filter.search_keyword` just before the request (see {@link normalizeMeetingsGraphqlInputVariables}).
 */
export type SupergraphMeetingsPageListInput = any
export type SupergraphMeetingsConnection = MeetingsGraphQLResult
export type SupergraphMeeting = GraphQLMeetingResult
export type SupergraphGetMeetingArg = {
  id: string
  organizationUuid?: string
  scopeType?: string | null
  scopeId?: string | null
  listMeetingsScopeType?: string | null
  ownerType?: string | null
  userIds?: string[]
}
export type SupergraphGetMeetingByEitherIdArg = { meetingId: string }
export type SupergraphGetScheduledEventArg = { id: string }
export type SupergraphGetMeetingRecapPageArg = { recapUuid: string }
export type SupergraphMeetingRecapPageResult = ReturnType<typeof mapMeetingRecapPageResponse>
export type SupergraphFilterCountPath = 'listMeetings' | 'meetingsPageList'
export type SupergraphFilterCountRequest = {
  path: SupergraphFilterCountPath
  inputs: SupergraphListMeetingsInput[] | SupergraphMeetingsPageListInput[]
}

/** Right-rail / refresh single meeting via cmd `listMeetings` + `filter.uuids` and list scope. */
export const buildMeetingByIdSupergraphInput = (
  id: string,
  scope?: ListMeetingsScopeUiFilters,
) => ({
  filter: { uuids: [id] },
  scope: buildListMeetingsScope(scope),
  pagination: { first: 1, after: null },
})

/** Recap / foreign-event lookup via cmd `listMeetings` + `filter.externalUuids`. */
export const buildMeetingByEitherIdSupergraphInput = (meetingId: string) => ({
  filter: {
    externalUuids: [meetingId],
    uuids: [meetingId],
  },
  pagination: { first: 1, after: null },
})

/** From `SUPERGRAPH_BASE_URL` at webpack build (`define.mjs`); else `/api/supergraph/graphql`. */
export const getSupergraphBaseUrl = () => {
  const value = typeof window !== 'undefined'
    && (window as any).SUPERGRAPH_BASE_URL

  return (value && String(value)) || '/api/supergraph/graphql'
}

/** Count connection results by counting returned meeting nodes (no `total` on connection). */
const countConnectionMeetingNodes = (connection: any): number => {
  const meetings = connection?.meetings
  if (!Array.isArray(meetings)) return 0
  return meetings.filter((edge: any) => edge?.node?.uuid).length
}

/** cmd `Meetings_CMDMeeting` node selection (shared by `listMeetings` and `meetingsPageList`). */
const MEETING_NODE_FIELDS = `
  uuid
  kind
  ownerUuid
  title
  startTime
  endTime
  location
  locationKind
  cancelled
  externalSystemId
  inviteesCount
  inviteesLimit

  eventType {
    uuid
    name
    color
    duration
    kind
  }

  participants {
    email
    identifier
    name
    noShow
    rescheduled
    role
    status
  }

  recapStatus {
    uuid
    status
    error
    summary
  }
`

const CONNECTION_FIELDS = `
  meetings {
    cursor
    node {
      ${MEETING_NODE_FIELDS}
    }
  }
  pageInfo {
    startCursor
    endCursor
    hasNextPage
    hasPreviousPage
  }
`

// istanbul ignore next
const ListMeetingsDocument = `
  query ListMeetings($input: Meetings_ListMeetingsInput!) {
    listMeetings(input: $input) {
      ${CONNECTION_FIELDS}
    }
  }
`

// istanbul ignore next
const MeetingsPageListDocument = `
  query MeetingsPageList($input: MeetingsSearch_MeetingsInput!) {
    meetingsPageList(input: $input) {
      ${CONNECTION_FIELDS}
    }
  }
`

const MeetingsPageListCountDocument = `
  query MeetingsPageListCount($input: MeetingsSearch_MeetingsInput!) {
    meetingsPageList(input: $input) {
      meetings {
        node {
          uuid
        }
      }
    }
  }
`

const ListMeetingsCountDocument = `
  query ListMeetingsCount($input: Meetings_ListMeetingsInput!) {
    listMeetings(input: $input) {
      meetings {
        node {
          uuid
        }
      }
    }
  }
`

const GetScheduledEventDocument = `
  query GetScheduledEvent($id: ID!) {
    getScheduledEvent(id: $id) {
      event
    }
  }
`

/**
 * Minimal field set for TrackedMeetingsDetails (title, attendees, status/video,
 * transcript + summary sections). Template switching stays on REST
 * (`TemplateSummaryProvider`); do not add unused MI metadata here.
 */
// istanbul ignore next
const MeetingRecapPageDocument = `
  query MeetingRecapPage(
    $recapUuid: ID!
    $trackedMeetingUuid: MI_UUID!
    $summaryUuid: String!
  ) {
    meetingForRecap(recapUuid: $recapUuid) {
      uuid
      ownerUuid
      kind
      title
      startTime
      endTime
      location
      participants {
        identifier
        email
        name
        noShow
        rescheduled
        role
        status
      }
    }

    get_tracked_meeting(
      uuid: $trackedMeetingUuid
      get_signed_urls: true
    ) {
      ... on MI_get_tracked_meeting_200_response {
        resource {
          uuid
          status
          error
          thumbnail
          video_url
          recap_permission
          user_uuid
          name
          foreign_event_id
          foreign_event_provider
          location
          start_time
          end_time
        }
      }
    }

    get_tracked_meeting_summary(uuid: $summaryUuid) {
      ... on MI_get_tracked_meeting_summary_200_response {
        resource {
          transcript {
            speaker
            words
            startTime
            endTime
          }
          summaries {
            ... on MI_Summary {
              name
              kind
              value {
                ... on MI_ActionItems {
                  id
                  description
                  checked
                }
                ... on MI_String_container {
                  String
                }
              }
            }
          }
        }
      }
    }
  }
`

export const meetingsSupergraphApi = rootApi
  .enhanceEndpoints({ addTagTypes: MEETINGS_TAG_TYPES })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      // Default meetings page / scope / non-recap filters → cmd-api `listMeetings`.
      listMeetingsSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphListMeetingsInput
      >({
        query: (input: SupergraphListMeetingsInput) => ({
          document: ListMeetingsDocument,
          variables: { input: normalizeMeetingsGraphqlInputVariables(input) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      // Keyword search / recap (meeting-intelligence) filter → search-then-filter `meetingsPageList`.
      meetingsPageListSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphMeetingsPageListInput
      >({
        query: (input: SupergraphMeetingsPageListInput) => ({
          document: MeetingsPageListDocument,
          variables: { input: normalizeMeetingsGraphqlInputVariables(input) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      // Filter-modal / action-bar count → listMeetings (default) or meetingsPageList (search/recap).
      searchMeetingsSupergraphFilterCount: build.query<
        number,
        SupergraphFilterCountRequest
      >({
        async queryFn({ path, inputs }, _api, _extraOptions, baseQuery) {
          const document = path === 'meetingsPageList'
            ? MeetingsPageListCountDocument
            : ListMeetingsCountDocument
          const rootField = path === 'meetingsPageList' ? 'meetingsPageList' : 'listMeetings'
          const countMeta = {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          }

          const results = await Promise.all((inputs ?? []).map((rawInput) => baseQuery({
            document,
            variables: {
              input: normalizeMeetingsGraphqlInputVariables(rawInput),
            },
            meta: countMeta,
          })))

          const failed = results.find((result) => result.error)
          if (failed?.error) {
            return { error: failed.error }
          }

          const total = results.reduce(
            (sum, result) => sum + countConnectionMeetingNodes((result.data as any)?.[rootField]),
            0,
          )

          return { data: total }
        },
        providesTags: [buildMeetingListTag()],
      }),

      // Right-rail single meeting → cmd-api `listMeetings` filtered by `uuids`.
      getMeetingSupergraph: build.query<
        SupergraphMeeting | null,
        SupergraphGetMeetingArg
      >({
        query: ({
          id,
          scopeType,
          scopeId,
          listMeetingsScopeType,
          ownerType,
          userIds,
        }) => ({
          document: ListMeetingsDocument,
          variables: {
            input: buildMeetingByIdSupergraphInput(id, {
              scopeType,
              scopeId,
              listMeetingsScopeType,
              ownerType,
              userIds,
            }),
          },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetMeetingSupergraphResponse,
        providesTags: (result, _error, args) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.id) {
            tags.push(buildMeetingTag(args.id))
          }
          return tags
        },
        extraOptions: { handleErrors: false },
      }),

      // Right-rail publisher enrichment → monolith scheduled event via supergraph `getScheduledEvent`.
      getScheduledEventSupergraph: build.query<
        Record<string, unknown> | null,
        SupergraphGetScheduledEventArg
      >({
        query: ({ id }) => ({
          document: GetScheduledEventDocument,
          variables: { id },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetScheduledEventSupergraphResponse,
        providesTags: (_result, _error, args) => (
          args?.id ? [buildMeetingTag(args.id)] : [buildMeetingListTag()]
        ),
        extraOptions: { handleErrors: false },
      }),

      // Recap / foreign-event single meeting → cmd-api `listMeetings` filtered by `externalUuids`.
      getMeetingByEitherIdSupergraph: build.query<
        SupergraphMeeting | null,
        SupergraphGetMeetingByEitherIdArg
      >({
        query: ({ meetingId }) => ({
          document: ListMeetingsDocument,
          variables: { input: buildMeetingByEitherIdSupergraphInput(meetingId) },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetMeetingSupergraphResponse,
        providesTags: (result, _error, args) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.meetingId) {
            tags.push(buildMeetingTag(args.meetingId))
          }
          return tags
        },
        extraOptions: { handleErrors: false },
      }),

      // Notetaker single recap details page → meetingForRecap + tracked meeting + summary.
      getMeetingRecapPage: build.query<
        SupergraphMeetingRecapPageResult,
        SupergraphGetMeetingRecapPageArg
      >({
        query: ({ recapUuid }) => ({
          document: MeetingRecapPageDocument,
          variables: {
            recapUuid,
            trackedMeetingUuid: recapUuid,
            summaryUuid: recapUuid,
          },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: mapMeetingRecapPageResponse,
        providesTags: (_result, _error, { recapUuid }) => [
          { type: MEETING_RECAP_PAGE_TAG_TYPE, id: recapUuid },
        ],
        extraOptions: { handleErrors: false },
      }),
    }),
  })

export const {
  useListMeetingsSupergraphQuery,
  useLazyListMeetingsSupergraphQuery,
  useMeetingsPageListSupergraphQuery,
  useLazyMeetingsPageListSupergraphQuery,
  useSearchMeetingsSupergraphFilterCountQuery,
  useLazySearchMeetingsSupergraphFilterCountQuery,
  useGetMeetingSupergraphQuery,
  useLazyGetMeetingSupergraphQuery,
  useGetScheduledEventSupergraphQuery,
  useGetMeetingByEitherIdSupergraphQuery,
  useGetMeetingRecapPageQuery,
} = meetingsSupergraphApi
